import { useQuery } from "@tanstack/react-query";
import {
  getAllAssetInsights,
  GetAllAssetInsightsReq,
  getAllSceneInsights,
  GetAllSceneInsightsReq,
  getAllVideoInsights,
  GetAllVideoInsightsReq,
  getVideoStudySummary,
  GetVideoStudySummaryReq,
} from "./api";
import { AssetInsight, SceneInsight, VideoInsight } from "./types/insight.type";
import { VideoStudySummary } from "./types/study-summary.type";

/*
 * useGetAllAssetInsights Query
 *
 * This query is used to get all insights for every asset in a study.
 *
 * Query Key: [projectId, studyId, "insights", sortBy, orderBy]
 */
export const useGetAllAssetInsights = ({ projectId, studyId, sortBy, orderBy }: GetAllAssetInsightsReq, options?: { enabled?: boolean }) => {
  return useQuery<AssetInsight[]>({
    queryKey: [projectId, studyId, "asset-insights", sortBy, orderBy],
    throwOnError: true,
    queryFn: () => getAllAssetInsights({ projectId, studyId, sortBy, orderBy }),
    ...options,
  });
};

/**
 * useGetAllSceneInsights Query
 *
 * This query is used to get all insights for every scene in a study.
 *
 * Query Key: [projectId, studyId, "scene-insights", sortBy, orderBy]
 */
export const useGetAllSceneInsights = ({ projectId, studyId, sortBy, orderBy }: GetAllSceneInsightsReq, options?: { enabled?: boolean }) => {
  return useQuery<SceneInsight[]>({
    queryKey: [projectId, studyId, "scene-insights", sortBy, orderBy],
    throwOnError: true,
    queryFn: () => getAllSceneInsights({ projectId, studyId, sortBy, orderBy }),
    ...options,
  });
};

/**
 * useGetAllVideoInsights Query
 *
 * This query is used to get all insights for every video in a study.
 *
 * Query Key: [projectId, studyId, "video-insights", sortBy, orderBy]
 */
export const useGetAllVideoInsights = ({ projectId, studyId, sortBy, orderBy }: GetAllVideoInsightsReq, options?: { enabled?: boolean }) => {
  return useQuery<VideoInsight[]>({
    queryKey: [projectId, studyId, "video-insights", sortBy, orderBy],
    throwOnError: true,
    queryFn: () => getAllVideoInsights({ projectId, studyId, sortBy, orderBy }),
    ...options,
  });
};

/**
 * useGetVideoStudySummary Query
 *
 * This query is used to get the study-level Copilot summary for a video study. Mocked until
 * the pipeline returns a generated summary. Unlike the per-asset/scene insights above, this is
 * study-level, so it is its own query rather than a field on VideoInsight.
 *
 * Query Key: [projectId, studyId, "video-study-summary"]
 */
export const useGetVideoStudySummary = ({ projectId, studyId }: GetVideoStudySummaryReq, options?: { enabled?: boolean }) => {
  return useQuery<VideoStudySummary>({
    queryKey: [projectId, studyId, "video-study-summary"],
    throwOnError: true,
    queryFn: () => getVideoStudySummary({ projectId, studyId }),
    ...options,
  });
};
